import { useCallback, useEffect, useState } from 'react'

/**
 * Arms a conversation reset and drains it as soon as the chat is idle.
 *
 * A settings save must never reset mid-turn: the reply already in flight belongs to
 * the conversation that asked for it, and clearing out from under it is the hazard the
 * shells' own `resetDisabled` / `canReset` guards exist to prevent.
 *
 *   context: when nothing is in flight — the common case — the drain runs on the
 *   render that armed it, so it reads as immediate rather than deferred.
 *   context: `blocked` must come from the LIVE chat source, never the selected one. A
 *   scripted transcript carries an `awaitingResponse` of its own, which would defer a
 *   free-phase reset for a turn the merchant is only watching. This is the same
 *   discipline WizardSimulator's `resetDisabled = live.awaitingResponse` applies.
 *   context: blocking the SAVE instead was rejected — the merchant's intent is to
 *   save, and a Save that is intermittently unavailable for reasons living in the
 *   phone beside it is opaque.
 *   context: DEPLOY DEPENDENCY — merchant-api must mint a fresh chat id per conversation
 *   (nexus #1378 / AI-860, merged 2026-08-17). This is the one authoritative statement of
 *   it; call sites point here. Against an older backend that recycles one id per
 *   subscription the failure is a HANG, not a quiet no-op: reset() adds the live id to
 *   useRecoveryChat's supersededChatIdsRef, and its frame filter then drops everything
 *   stamped with that id except connection_established and error — so the recycled "new"
 *   chat never clears awaitingResponse and the phone sits on a permanent "…". Staging is
 *   this combination today. `connection_established` being re-sent after a reset is the
 *   observable signal that the backend really did open a new chat.
 */
export const usePendingConversationReset = ({
  reset,
  blocked,
  enabled = true,
}: {
  reset: () => void
  // A reply is inbound. Read this from the live source (see context above).
  blocked: boolean
  // Surfaces opt out — the wizard's guided phase, whose transcript never responded to
  // settings in the first place.
  enabled?: boolean
}): (() => void) => {
  const [pending, setPending] = useState(false)

  const armReset = useCallback(() => {
    if (!enabled) return
    setPending(true)
  }, [enabled])

  useEffect(() => {
    if (!pending || blocked || !enabled) return
    // Cleared BEFORE the reset, not after: reset() re-renders its host, and a drain
    // that cleared afterwards could re-enter and fire a second time.
    setPending(false)
    reset()
  }, [pending, blocked, enabled, reset])

  return armReset
}

export default usePendingConversationReset
